import '@naturalcycles/ncuikit/dist/ButtonLink.css'
import { _isNotNullish } from '@naturalcycles/js-lib'
import { ButtonLink, ButtonLinkProps } from '@naturalcycles/ncuikit'
import { Goal } from '@naturalcycles/shared'
import {
  ButtonFontSizes,
  ButtonProps,
  ButtonSizes,
  ButtonThemes,
} from '@src/components/button/Button.component'
import { campaignContext } from '@src/context/campaign.context'
import { languageContext } from '@src/context/language.context'
import { addQueryStringAndNavigate, enhanceSignupUrl, getSignupURL } from '@src/helpers/signupUrl'
import { useAppSelector } from '@src/hooks/useAppSelector'
import { mixpanelService } from '@src/srv/mixpanel.service'
import type { MouseEvent, ReactElement } from 'react'
import { useCallback, useContext, useMemo } from 'react'

/**
 * TODO:
 * This component should be refactored so that it doesn't prevent default browser behaviour on click.
 * Everything that we do (compute href, add query params, utms, etc) should be done beforehand so that the final URL is provided to the button/link
 * and it is not necessary anymore to prevent default behaviour.
 *
 * - there is an exception: when a "customClickHandler" is provided. At current time this is provided only ONCE in the entire codebase (PopupOfferCampaign.component.tsx)
 * - event tracking should also be done "at destination" (i.e. in the web signup page) instead of here. WebSignup can track which button has been clicked on NCWeb based on utm parames added to the URL (see ncUtmSource)
 * - code that depends on CookieBot (eg. window.Cookiebot?.hasResponse) should listen to cookiebot events (CookiebotOnLoad) and perform actions when event is triggered
 *
 * A partial refactory has been done on Apr 22 2025, but did not go all the way as it is outside of the scope of the task.
 */

interface SignupButtonProps {
  id?: string
  uid?: string
  children: any
  elementName?: string
  size?: ButtonSizes
  theme?: ButtonThemes
  underline?: boolean
  code?: string | undefined
  ignoreConfirmation?: boolean | undefined
  ncGoal?: Goal | undefined
  fontSize?: ButtonFontSizes
  ncUtmSetSource?: string
  signupUrlPath?: string
  /**
   * Code that runs on click before redirecting to the signup page.
   * If this function returns false, the redirect will not happen.
   */
  onClick?: ButtonProps['onClick']
  // TODO: bring this property to top level and remove all the unused properties from old button implementation
  refactory: ButtonLinkProps | null
}

export function SignupButton({
  id,
  uid,
  children,
  elementName = 'SignupButton',
  size = 'regular',
  theme = 'brandPurple',
  underline = false,
  code: codeParam,
  ignoreConfirmation: ignoreConfirmationParam,
  ncGoal,
  fontSize = ButtonFontSizes.RegularText,
  signupUrlPath,
  ncUtmSetSource,
  refactory,
  onClick: customClickHandler,
}: SignupButtonProps): ReactElement {
  const campaign = useContext(campaignContext)
  const languageContextLocale = useContext(languageContext).locale
  const discountCode = useAppSelector(state => state.discountCode.code)
  const savedDiscountCode =
    typeof window !== 'undefined' && window.sessionStorage.getItem('discountCode')
  const popupCode = discountCode || savedDiscountCode
  const reduxReferral = useAppSelector(state => state.discountCode.referral)
  const reduxName = useAppSelector(state => state.discountCode.name)

  function handleClick(event: MouseEvent): boolean | void | Promise<void> {
    event.preventDefault()
    return handleClick2(event)
  }

  const locale = useMemo(
    () =>
      getFirstDefinedElement([
        // The campaign locale has a higher priority than the LanguageContext locale
        // for pages such as /c/plan-offer-uk which whose filename ends in en-us.tsx
        // but which still needs the en-GB locale in its signup links.
        campaign?.locale,
        // Fall back to the general-purpose site-wide LanguageContext locale for
        // 99% of signup buttons.
        languageContextLocale,
      ]),
    [campaign, languageContextLocale],
  )

  const code = useMemo(
    () =>
      getFirstDefinedElement([
        // The code from the props gets the highest priority so that landing pages
        // with multiple codes such as /c/choose-your-offer can specify a different
        // code on one specific signup button.
        codeParam,
        // Fall back to the CampaignContext code for the majority of landing page
        // signup buttons.
        campaign?.code,
        // Last priority is checking for coupon codes stored in the global state or the session storage coming from popups
        popupCode,
      ]),
    [campaign, popupCode, codeParam],
  )

  const ignoreConfirmation = useMemo(
    () =>
      getFirstDefinedElement([
        // The value from the props gets the highest priority so that the behaviour
        // of the `ignoreConfirmation` value matches that of `code`.
        ignoreConfirmationParam,
        // Fall back to the CampaignContext value for the majority of landing page
        // signup buttons.
        campaign?.ignoreConfirmation,
      ]),
    [campaign, ignoreConfirmationParam],
  )

  const href = useMemo(() => {
    const signupUrl = getSignupURL(signupUrlPath || `${locale}/signup`)
    return enhanceSignupUrl(signupUrl, ncGoal, reduxReferral, reduxName, ignoreConfirmation, code)
  }, [ncGoal, locale, reduxReferral, reduxName, ignoreConfirmation, code, signupUrlPath])

  const handleClick2 = useCallback(
    (event: MouseEvent): boolean | void | Promise<void> => {
      if (isPromise(customClickHandler)) {
        return customClickHandler
          .then(() => addQueryStringAndNavigate(href, ncUtmSetSource))
          .then(() => {})
      }

      // if the custom handler returns false, it means that the redirect happens inside customClickHandler
      return customClickHandler?.(event) === false
        ? false
        : addQueryStringAndNavigate(href, ncUtmSetSource)
    },
    [customClickHandler, href, ncUtmSetSource],
  )

  const onAfterClickAndRedirect = useCallback(
    async (
      e: MouseEvent,
      attrs: Pick<ButtonLinkProps, 'id' | 'uid' | 'target' | 'href'>,
    ): Promise<void> => {
      // if this function returns false, it means that the redirect happens inside handleClick, and therefore we should not continue
      if ((await handleClick2(e)) === false) {
        return
      }

      if (!elementName) {
        return
      }

      if (!attrs.href) {
        mixpanelService.trackClick(elementName)
        return
      }

      e.preventDefault()
      mixpanelService.trackLink(elementName, attrs.href)
    },
    [elementName],
  )

  const _buttonProps: ButtonProps = {
    elementName,
    fontSize,
    href,
    onClick: handleClick,
    size,
    theme,
    underline,
    id,
    uid,
  }

  const newButtonProps: ButtonLinkProps = {
    href,
    // TODO: find better naming for this property, could just be "handleClick"
    onAfterClickAndRedirect,
    ariaLabel: 'Signup',
    ...refactory,
  }

  return (
    <>
      {locale && _isNotNullish(children) && (
        // Signup button should be ClientOnly to avoid hydration issues, but we need it not to be for
        // tests right now. Todo: Move the signup button tests to be e2e tests.
        <>
          <ButtonLink {...newButtonProps}>{children}</ButtonLink>
          {/* TODO: Clean up. Old button implementation: keep for reference */}
          {/* <Button {...buttonProps} onClick={handleClick} suppressHydrationWarning={true}>
            {children} old
          </Button> */}
        </>
      )}
      {/* Placeholder for CLS */}
      {!locale && <div style={{ height: '46px', width: '100%' }} />}
    </>
  )
}

function getFirstDefinedElement(list: unknown[]): any {
  return list.find(v => v !== undefined)
}

function isPromise(value: any): value is Promise<any> {
  return value && typeof value.then === 'function'
}
